// NavBar.tsx — v3 · Larger Products panel with touchable glass
import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Moon, Sun, Menu, X, ExternalLink, ChevronDown, Activity, Cpu } from 'lucide-react'
import SpinningLogo from './SpinningLogo'

let PRODUCT_URL: string;
if (!import.meta.env.VITE_NODE_ENV || import.meta.env.VITE_NODE_ENV === "dev") {
  PRODUCT_URL = "http://localhost:5173/stocksignalspro#home";
} else {
  PRODUCT_URL = import.meta.env.VITE_PRODUCT_URL || "https://stocksignalspro.larxnet.com/";
}

interface NavBarProps {
  dark: boolean
  setDark: (v: boolean) => void
}

const NavBar: React.FC<NavBarProps> = ({ dark, setDark }) => {
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setProductsOpen(false)
  }, [location.pathname])

  const isActive = (path: string) => location.pathname === path

  // Slight delay on close so mouse moving to panel doesn't flicker
  const openProducts = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setProductsOpen(true)
  }
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setProductsOpen(false), 120)
  }

   const navLinkStyle = (path: string): React.CSSProperties => ({
      color: isActive(path) ? 'var(--c-cyan)' : 'var(--c-text-2)',
      transition: 'color 0.2s ease',
    })
  
    const hoverIn = (e: React.MouseEvent<HTMLElement>) => {
      const el = e.currentTarget
      if (!el.dataset.active) el.style.color = 'var(--c-text)'
    }
    const hoverOut = (e: React.MouseEvent<HTMLElement>, path?: string) => {
      const el = e.currentTarget
      el.style.color = path && isActive(path) ? 'var(--c-cyan)' : 'var(--c-text-2)'
    }
  

  return (
    <nav
      className='sticky top-0 z-50 transition-all duration-300'
      style={{
        background: scrolled
          ? dark
            ? "rgba(28,30,42,0.92)"
            : "rgba(238,241,247,0.92)"
          : "transparent",
        backdropFilter: scrolled ? "blur(24px) saturate(1.5)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(24px) saturate(1.5)" : "none",
        borderBottom: `1px solid ${scrolled ? "var(--c-border-md)" : "transparent"}`,
        boxShadow: scrolled ? "0 4px 32px rgba(0,0,0,0.22)" : "none",
      }}
    >
      <div className='max-w-7xl mx-auto px-6 py-4'>
        <div className='flex items-center justify-between'>
          {/* Logo */}
          <Link to='/' className='flex-shrink-0'>
            <SpinningLogo size='md' textSize='text-xl' />
          </Link>

          {/* Desktop nav */}
          <div className='hidden md:flex items-center gap-1'>
            <Link
              to='/'
              className='px-4 py-2 rounded-xl text-md font-medium transition-colors duration-200'
              /* style={{ color: isActive('/') ? 'var(--c-cyan)' : 'var(--c-text-2)' }} */
              style={navLinkStyle("/")}
              onMouseEnter={hoverIn}
              onMouseLeave={(e) => hoverOut(e, "/")}
            >
              Home
            </Link>

            {/* ── Products dropdown ───────────────────────── */}
            <div className='relative' onMouseEnter={openProducts} onMouseLeave={scheduleClose}>
              <button
                className='flex items-center gap-1.5 px-4 py-2 rounded-xl text-md font-medium transition-colors duration-200'
                style={{
                  color: productsOpen ? "var(--c-cyan)" : "var(--c-text-2)",
                  background: productsOpen ? "rgba(34,211,238,0.07)" : "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Products
                <ChevronDown
                  className='w-3.5 h-3.5 transition-transform duration-200'
                  style={{ transform: productsOpen ? "rotate(180deg)" : "none" }}
                />
              </button>

              {/* ── Big glass panel ── */}
              {productsOpen && (
                <div
                  className='absolute top-full left-1/2 -translate-x-1/2 mt-3  rounded-2xl overflow-hidden'
                  style={{
                    width: 420,
                    background: "rgba(19,20,27,0.96)",
                    backdropFilter: "blur(24px)",
                    WebkitBackdropFilter: "blur(24px)",
                    border: "1px solid var(--c-border-md)",
                    borderTopColor: "var(--c-border-hi)",
                    boxShadow: "0 20px 50px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.06) inset",
                  }}
                  onMouseEnter={openProducts}
                  onMouseLeave={scheduleClose}
                >
                  {/* Subtle pointer arrow */}
                  <div
                    style={{
                      position: "absolute",
                      top: -6,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 12,
                      height: 6,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        background: "rgba(30,32,48,0.98)",
                        border: "1px solid var(--c-border-md)",
                        transform: "rotate(45deg) translate(3px, 3px)",
                      }}
                    />
                  </div>

                  <div
                    className='rounded-2xl overflow-hidden'
                    style={{
                      background: dark ? "rgba(22,24,38,0.98)" : "rgba(245,246,252,0.98)",
                      backdropFilter: "blur(32px) saturate(1.5)",
                      WebkitBackdropFilter: "blur(32px) saturate(1.5)",
                      border: "1px solid var(--c-border-md)",
                      borderTopColor: "rgba(34,211,238,0.30)",
                      boxShadow:
                        "0 32px 64px rgba(0,0,0,0.50), 0 1px 0 rgba(255,255,255,0.07) inset",
                    }}
                  >
                    {/* Cyan shimmer top line */}
                    <div
                      style={{
                        height: 2,
                        background:
                          "linear-gradient(90deg, transparent 5%, #22d3ee 50%, transparent 95%)",
                        opacity: 0.7,
                      }}
                    />

                    <div className='p-4 space-y-2'>
                      {/* Section label */}
                      <div
                        className='px-2 pb-1 text-xs font-semibold uppercase tracking-widest'
                        style={{ color: "var(--c-text-3)", fontFamily: "var(--font-mono)" }}
                      >
                        Our Products
                      </div>

                      {/* ── StockSignals Pro ── LIVE · fully clickable */}
                      <a
                        href={PRODUCT_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='group flex items-center gap-4 p-4 rounded-xl transition-all duration-200'
                        style={{
                          textDecoration: "none",
                          background: "rgba(34,211,238,0.04)",
                          border: "1px solid rgba(34,211,238,0.10)",
                          display: "flex",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(34,211,238,0.10)";
                          e.currentTarget.style.borderColor = "rgba(34,211,238,0.28)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "rgba(34,211,238,0.04)";
                          e.currentTarget.style.borderColor = "rgba(34,211,238,0.10)";
                        }}
                      >
                        {/* Icon */}
                        <div
                          className='flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center'
                          style={{
                            background: "linear-gradient(135deg, #0891b2, #0d9488)",
                            boxShadow:
                              "0 4px 16px rgba(8,145,178,0.35), 0 0 0 1px rgba(34,211,238,0.20)",
                          }}
                        >
                          <Activity className='w-6 h-6 text-white' />
                        </div>
                        {/* Text */}
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2 mb-1'>
                            <span className='text-sm font-bold' style={{ color: "var(--c-text)" }}>
                              StockSignals Pro
                            </span>
                            <span
                              className='badge-live flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold'
                              style={{ fontFamily: "var(--font-mono)" }}
                            >
                              ● Live
                            </span>
                          </div>
                          <p
                            className='text-xs leading-relaxed'
                            style={{ color: "var(--c-text-2)" }}
                          >
                            Real-time signals & AI-powered analytics
                          </p>
                        </div>
                        {/* Arrow */}
                        <ExternalLink
                          className='flex-shrink-0 w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5'
                          style={{ color: "var(--c-cyan)" }}
                        />
                      </a>

                      {/* Divider */}
                      <div
                        style={{ height: 1, background: "var(--c-border)", margin: "4px 8px" }}
                      />

                      {/* ── DataAgents ── In Dev · not clickable */}
                      <div
                        className='flex items-center gap-4 p-4 rounded-xl'
                        style={{
                          background: "rgba(245,158,11,0.03)",
                          border: "1px solid rgba(245,158,11,0.10)",
                          cursor: "default",
                          opacity: 0.65,
                        }}
                      >
                        <div
                          className='flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center'
                          style={{
                            background: "rgba(245,158,11,0.08)",
                            border: "1px solid rgba(245,158,11,0.22)",
                          }}
                        >
                          <Cpu className='w-6 h-6' style={{ color: "var(--c-amber)" }} />
                        </div>
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-2 mb-1'>
                            <span className='text-sm font-bold' style={{ color: "var(--c-text)" }}>
                              DataAgents
                            </span>
                            <span
                              className='badge-dev flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold'
                              style={{ fontFamily: "var(--font-mono)" }}
                            >
                              In Dev
                            </span>
                          </div>
                          <p
                            className='text-xs leading-relaxed'
                            style={{ color: "var(--c-text-2)" }}
                          >
                            Autonomous AI agents for enterprise data pipelines
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Bottom footer link */}
                    <div
                      className='px-4 py-3 flex items-center justify-between'
                      style={{
                        borderTop: "1px solid var(--c-border)",
                        background: "rgba(0,0,0,0.12)",
                      }}
                    >
                      <span className='text-xs' style={{ color: "var(--c-text-3)" }}>
                        More products coming soon
                      </span>
                      <Link
                        to='/contact'
                        className='text-xs font-medium transition-colors duration-200'
                        style={{ color: "var(--c-cyan)" }}
                      >
                        Follow roadmap →
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              to='/contact'
              /*  className='px-4 py-2 rounded-xl text-md font-medium transition-colors duration-200'
              style={{ color: isActive("/contact") ? "var(--c-cyan)" : "var(--c-text-2)" }} */
              className='px-4 py-2 rounded-xl text-md font-medium'
              style={navLinkStyle("/contact")}
              onMouseEnter={hoverIn}
              onMouseLeave={(e) => hoverOut(e, "/contact")}
            >
              Contact
            </Link>
            <Link
              to='/pricing'
              className='px-4 py-2 rounded-xl text-md font-medium'
              style={navLinkStyle("/pricing")}
              onMouseEnter={hoverIn}
              onMouseLeave={(e) => hoverOut(e, "/pricing")}
            >
              Pricing
            </Link>
          </div>

          {/* Right actions */}
          <div className='hidden md:flex items-center gap-2'>
            <button
              onClick={() => setDark(!dark)}
              className='p-2 rounded-xl transition-all duration-200'
              style={{
                color: "var(--c-text-3)",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.06)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              aria-label='Toggle theme'
            >
              {dark ? <Sun className='w-5 h-5' /> : <Moon className='w-5 h-5' />}
            </button>

            <Link
              to='/login'
              className='px-4 py-2 rounded-xl text-md font-medium transition-colors duration-200'
              style={{ color: "var(--c-text-2)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--c-text)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--c-text-2)")}
            >
              Sign In
            </Link>

            <Link to='/register' className='btn-cta px-5 py-2.5 rounded-xl text-md font-semibold'>
              Register
            </Link>
          </div>

          {/* Mobile toggle */}
          <div className='flex md:hidden items-center gap-2'>
            <button
              onClick={() => setDark(!dark)}
              className='p-2 rounded-xl'
              style={{
                color: "var(--c-text-3)",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {dark ? <Sun className='w-5 h-5' /> : <Moon className='w-5 h-5' />}
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className='p-2 rounded-xl'
              style={{
                color: "var(--c-text-2)",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {mobileOpen ? <X className='w-6 h-6' /> : <Menu className='w-6 h-6' />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            className='md:hidden mt-4 pt-4 space-y-1'
            style={{ borderTop: "1px solid var(--c-border)" }}
          >
            {[
              { to: "/", label: "Home" },
              { to: "/contact", label: "Contact" },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className='block px-4 py-3 rounded-xl text-sm font-medium'
                style={{
                  color: isActive(to) ? "var(--c-cyan)" : "var(--c-text-2)",
                  background: isActive(to) ? "rgba(34,211,238,0.06)" : "transparent",
                  border: isActive(to)
                    ? "1px solid rgba(34,211,238,0.14)"
                    : "1px solid transparent",
                }}
              >
                {label}
              </Link>
            ))}

            <div className='glass rounded-xl overflow-hidden'>
              <div
                className='px-4 py-2 text-xs font-semibold uppercase tracking-wider'
                style={{ color: "var(--c-text-3)", fontFamily: "var(--font-mono)" }}
              >
                Products
              </div>
              <a
                href={PRODUCT_URL}
                target='_blank'
                rel='noopener noreferrer'
                className='flex items-center gap-3 px-4 py-3 text-sm font-medium'
                style={{
                  color: "var(--c-text-2)",
                  borderTop: "1px solid var(--c-border)",
                  textDecoration: "none",
                }}
              >
                <Activity className='w-4 h-4' style={{ color: "var(--c-cyan)" }} />
                StockSignals Pro
                <ExternalLink
                  className='w-3.5 h-3.5 ml-auto'
                  style={{ color: "var(--c-text-3)" }}
                />
              </a>
              <div
                className='flex items-center gap-3 px-4 py-3 text-sm font-medium'
                style={{
                  color: "var(--c-text-3)",
                  borderTop: "1px solid var(--c-border)",
                  opacity: 0.6,
                }}
              >
                <Cpu className='w-4 h-4' style={{ color: "var(--c-amber)" }} />
                DataAgents
                <span className='badge-dev ml-auto px-2 py-0.5 rounded-full text-xs'>In Dev</span>
              </div>
            </div>

            <div className='h-px my-2' style={{ background: "var(--c-border)" }} />

            <Link
              to='/login'
              className='block px-4 py-3 rounded-xl text-sm font-medium text-center'
              style={{ color: "var(--c-text-2)" }}
            >
              Sign In
            </Link>
            <Link
              to='/register'
              className='btn-cta block px-4 py-3 rounded-xl text-sm font-semibold text-center'
            >
              Get Started Free
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}

export default NavBar
